import { useAddSkills, useGetSkills } from "@features/wx/fieldForce/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

interface AddSkillComponentProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddSkillComponent: React.FC<AddSkillComponentProps> = ({
  isOpen,
  onClose,
}) => {
  const { id } = useParams<{ id: string }>();
  const { data: skills } = useGetSkills();
  const addSkills = useAddSkills(id);
  
  const skillOptions = useMemo(() => {
    if (!skills || !Array.isArray(skills)) {
      return [];
    }
    return skills.map((data) => ({
      value: data.id,
      label: data.skill,
    }));
  }, [skills]);

  const formFields: FormField[] = [
    {
      name: "skill",
      type: "select",
      label: "Skill",
      placeholder: "Select skill",
      required: true,
      options: skillOptions,
      className: "col-span-1",
      disabled: false,
      classes: {
        container: "w-full mb-4 relative",
        label: "block text-sm font-medium text-gray-700 mb-1",
        input:
          "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
      },
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: { skill: "" },
  });

  const handleReset = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = (data: any) => {
    const payload = {
      skill: data.skill,
      is_skill: true,
    };
    
    addSkills.mutateAsync(payload, {
      onSuccess: () => {
        logEvent(`Field Force ${id} Skill Added`, {
          skill: data.skill
        })
      }
    });
    handleReset();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Skill</DialogTitle>
          <DialogDescription>
            Add a new skill for this technician.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            gridColumns={1}
            responsiveColumns={{
              sm: 1,
              md: 1,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: "Add Skill",
              resetButtonText: "Cancel",
              resetOnSubmit: true,
              spacing: "comfortable",
              classes: {
                form: "space-y-8",
                fieldGroup:
                  "bg-white border border-gray-200 rounded-lg p-6 shadow-sm",
                button: "px-6 py-2 rounded-md font-medium transition-colors",
                submitButton:
                  "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
                resetButton:
                  "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-sm text-gray-500 mt-1 flex",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};